import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import api from './api';
import { getFrameChoice } from './globalState';
import BackButton from './BackButton';

function LicencePlateConfirm() {
  /*
    Handles confirmation and submission for both:
      assetType === 'plate'  — the user's custom dealer plate (default)
      assetType === 'frame'  — the user's custom dealer frame (Custom path only)

    On success:
      plate → /thank-you       (session complete)
      frame → /upload-plates   (frame done; continue to plate workflow)

    Dealer identity travels in the session token; the form carries only the
    files, the frame choice, and comments.
  */
  const location = useLocation();
  const navigate = useNavigate();

  // 'frame' when user arrived from the custom frame upload path; 'plate' otherwise
  const assetType = location.state?.assetType || 'plate';

  // Present when user arrived via in-app camera flow → PhotoGallery path.
  // Empty array for the file-upload path (no IndexedDB photos to clean up).
  const allPhotoKeys = location.state?.allPhotoKeys || [];

  const [comments, setComments] = useState('');
  const [files, setFiles] = useState(location.state?.files || []);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  // Blob URLs for image previews, created in an effect so they are never
  // created during render (which causes revocation races in Strict Mode).
  const [previewURLs, setPreviewURLs] = useState([]);

  useEffect(() => {
    const urls = files.map(f =>
      f.type.startsWith('image/') ? URL.createObjectURL(f) : null
    );
    setPreviewURLs(urls);
    return () => {
      urls.forEach(url => { if (url) URL.revokeObjectURL(url); });
    };
  }, [files]);

  // Redirect back if no files (all deleted, or user hit the URL directly).
  // In an effect: navigating during render is a React error.
  useEffect(() => {
    if (files.length > 0) return;
    if (allPhotoKeys.length > 0) {
      navigate('/license-plate-photography', { replace: true, state: { assetType } });
    } else {
      const fallback = assetType === 'frame'
        ? '/quick-start-guide-frame'
        : '/quick-start-guide-plates';
      navigate(fallback, { replace: true });
    }
  }, [files, allPhotoKeys, assetType, navigate]);

  const handleConfirm = async () => {
    setIsTransferring(true);
    setError('');

    try {
      const frameChoice = getFrameChoice();
      const formData = new FormData();
      formData.append('assetType', assetType);
      formData.append('comments', comments);
      if (frameChoice) formData.append('frameChoice', frameChoice);
      files.forEach((file) => formData.append('files', file));

      await api.post('/api/store-plate-files', formData);

      if (assetType === 'frame') {
        // Custom frame submitted — continue to the plate upload workflow
        navigate('/upload-plates');
      } else {
        // Plate submitted — session complete. Pass allPhotoKeys so ThankYou
        // can clear any IndexedDB photos captured during this session.
        navigate('/thank-you', { state: { support: false, allPhotoKeys } });
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMessage = err.response?.data?.message || err.response?.data?.error;
      if (status === 401) {
        setSessionExpired(true);
        setError('Your session has expired. Please re-enter your Dealer ID - your files are still selected.');
      } else if ((status === 400 || status === 502) && serverMessage) {
        setError(serverMessage);
      } else {
        setError('An error occurred while sending files. Please try again.');
      }
      setIsTransferring(false);
    }
  };

  const handleBack = () => {
    const backRoute = assetType === 'frame'
      ? '/quick-start-guide-frame'
      : '/quick-start-guide-plates';
    navigate(backRoute, { state: { files } });
  };

  const handleDeleteFile = (indexToDelete) => {
    setFiles(prevFiles => prevFiles.filter((_, idx) => idx !== indexToDelete));
  };

  const titleLabel = assetType === 'frame'
    ? 'Confirm Your Frame Files'
    : 'Confirm Your Uploaded Files';

  return (
    <div className="PhotoConfirm">
      <h1 className="PhotoConfirm-title">{titleLabel}</h1>
      <p>Please review the files you selected before sending them.</p>

      {/* File list */}
      <ul className="PhotoConfirm-file-list">
        {files.map((file, index) => (
          <li key={index} className="PhotoConfirm-file-list-item">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </li>
        ))}
      </ul>

      {/* Image previews — URLs are created in a useEffect, never during render */}
      <div className="PhotoConfirm-grid">
        {files.map((file, index) => {
          const previewURL = previewURLs[index];
          if (!previewURL) return null;
          return (
            <div key={index} className="PhotoConfirm-item" style={{ position: 'relative' }}>
              <img src={previewURL} alt={file.name} className="PhotoConfirm-image" />
              <button
                type="button"
                className="PhotoConfirm-delete-btn-corner"
                aria-label={`Delete ${file.name}`}
                onClick={() => handleDeleteFile(index)}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      <textarea
        className="PhotoConfirm-comments"
        placeholder="Include any comments or questions you'd like to submit with your files..."
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        rows={3}
        maxLength={2000}
      />

      {isTransferring && (
        <div style={{ color: '#1976d2', marginBottom: '12px', fontWeight: 500 }}>
          Transferring files...
        </div>
      )}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      <div className="PhotoConfirm-actions">
        <button className="App-button" onClick={handleBack} disabled={isTransferring}>
          {assetType === 'frame' ? 'Upload more frame files' : 'Upload more files'}
        </button>
        {sessionExpired ? (
          <button className="App-button" onClick={() => navigate('/dealer-id')}>
            Re-enter Dealer ID
          </button>
        ) : (
          <button
            className="App-button"
            onClick={handleConfirm}
            disabled={files.length === 0 || isTransferring}
          >
            {assetType === 'frame' ? 'Confirm frame — next: plate' : 'Confirm and send files'}
          </button>
        )}
      </div>
      <BackButton />
    </div>
  );
}

export default LicencePlateConfirm;
